import { computed, onScopeDispose, readonly, ref, watch } from 'vue'
import { useToast } from '@/composables/useToast'
import { useCurrentUser } from '@/queries'
import { getTonWalletController } from './controller'
import { personalSession, setPersonalSession } from '@/queries/personal-session'
import { normalizeTonAddress } from '@/utils/ton-address'
import { apiClient } from '@/api/client'

let bindingsOwnerActive = false
let refetchCurrentUser: (() => Promise<unknown>) | null = null
const currentUserLoading = ref(false)

export const tonWalletBindingState = {
  isLoadingUser: readonly(currentUserLoading),
}

export const refreshTonWalletUser = async (): Promise<void> => {
  await refetchCurrentUser?.()
}

export const useTonWalletAppBindings = (): void => {
  if (bindingsOwnerActive) throw new Error('ton_wallet_app_bindings_already_installed')
  const wallet = getTonWalletController()
  bindingsOwnerActive = true

  const { success, error: showError } = useToast()
  wallet.commands.setCallbacks({
    onAuthExpired: () => {
      showError('Your session has expired. Please reconnect your wallet to continue.')
    },
    onConnectionSuccess: () => {
      success('Wallet connected successfully')
    },
    onConnectionError: (message) => {
      showError(message)
    },
  })

  const isAuthenticated = computed(() => wallet.selectors.isAuthenticated.value)
  const stopSessionSync = watch(
    () => {
      const address = normalizeTonAddress(wallet.selectors.address.value)
      const authenticatedWallet = normalizeTonAddress(wallet.selectors.user.value?.wallet)
      return isAuthenticated.value && address && address === authenticatedWallet ? address : null
    },
    (address) => {
      apiClient.invalidateAuthContext()
      setPersonalSession(address)
    },
    { immediate: true, flush: 'sync' }
  )
  const { currentUser, isLoading, refetch } = useCurrentUser({ enabled: isAuthenticated })
  refetchCurrentUser = refetch
  const stopUserSync = watch(
    currentUser,
    (user) => {
      if (
        user &&
        isAuthenticated.value &&
        normalizeTonAddress(user.wallet) === personalSession.value?.wallet
      )
        wallet.commands.setUser(user)
    },
    { immediate: true }
  )
  const stopLoadingSync = watch(
    isLoading,
    (loading) => {
      currentUserLoading.value = loading
    },
    { immediate: true }
  )

  onScopeDispose(() => {
    stopSessionSync()
    setPersonalSession(null)
    stopUserSync()
    stopLoadingSync()
    wallet.commands.setCallbacks({})
    refetchCurrentUser = null
    currentUserLoading.value = false
    bindingsOwnerActive = false
  })
}
